import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, Calendar, Store } from "lucide-react";
import { NAV_LINKS, LOGO_URL } from "@/data/content";
import { FranchiseDialog } from "@/components/FranchiseDialog";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [franchise, setFranchise] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "home") {
      if (location.pathname !== "/") navigate("/");
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(id), 120);
    } else {
      scrollTo(id);
    }
  };

  const openFranchise = () => {
    setOpen(false);
    setFranchise(true);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#f7f0eb]/90 backdrop-blur-xl border-b border-[#e8dcc8] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-2.5"
        >
          <motion.img
            src={LOGO_URL}
            alt="Bobo's Donuts"
            animate={{ rotate: [0, -8, 8, -6, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
            className="h-12 w-12 object-contain"
          />
          <span className="font-display font-bold text-[1.75rem] tracking-tight text-[#111111]">Bobo's<sup className="align-[0.42em] text-[0.5em] font-semibold">™</sup></span>
          <img
            src="/asl-love-hero.png?v=2"
            alt="ASL sign for I love you"
            data-testid="nav-asl-love"
            className="h-6 w-auto select-none -ml-1.5"
          />
        </button>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/about"
            data-testid="nav-about"
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-full font-medium text-[#3d3d3d] hover:text-[#111111] hover:bg-[#ff648b]/25 transition-colors"
          >
            About
          </Link>
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="px-4 py-2 rounded-full font-medium text-[#3d3d3d] hover:text-[#111111] hover:bg-[#ff648b]/25 transition-colors"
            >
              {l.label}
            </button>
          ))}
          {/* Temporarily unsurfaced — Franchising link (code preserved):
          <button
            data-testid="nav-franchising"
            onClick={openFranchise}
            className="px-4 py-2 rounded-full font-medium text-[#3d3d3d] hover:text-[#111111] hover:bg-[#ff648b]/25 transition-colors"
          >
            Franchising
          </button>
          */}
          <button
            data-testid="nav-book-btn"
            onClick={() => go("contact")}
            className="ml-2 px-5 py-2.5 rounded-full bg-[#ff648b] text-[#111111] font-bold hover:bg-[#ed4a76] hover:scale-105 transition-all shadow-md flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" /> Book the Truck
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-[#111111]"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#f7f0eb] border-t border-[#e8dcc8] px-5 py-4 flex flex-col gap-1">
          <Link
            to="/about"
            data-testid="mobile-nav-about"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 rounded-2xl font-medium text-[#111111] hover:bg-[#ff648b]/25"
          >
            About
          </Link>
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left px-4 py-3 rounded-2xl font-medium text-[#111111] hover:bg-[#ff648b]/25"
            >
              {l.label}
            </button>
          ))}
          {/* Temporarily unsurfaced — Franchising link (code preserved):
          <button
            data-testid="mobile-nav-franchising"
            onClick={openFranchise}
            className="text-left px-4 py-3 rounded-2xl font-medium text-[#111111] hover:bg-[#ff648b]/25 flex items-center gap-2"
          >
            <Store className="h-4 w-4" /> Franchising
          </button>
          */}
          <button
            data-testid="mobile-book-btn"
            onClick={() => go("contact")}
            className="mt-1 px-4 py-3 rounded-full bg-[#ff648b] text-[#111111] font-bold text-center"
          >
            Book the Truck
          </button>
        </div>
      )}
      <FranchiseDialog open={franchise} onOpenChange={setFranchise} />
    </header>
  );
};
